<template>
  <div class="test-execute-container page-container bg-light-gradient">
    <div class="container-padded py-4">
      <div v-if="loading" class="text-center py-5 content-loading">
        <div class="loader loader-lg mb-3">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Wird geladen...</span>
          </div>
        </div>
        <p class="text-muted">Test wird geladen...</p>
      </div>
      
      <div v-else-if="!testFound" class="text-center py-5 not-found-container">
        <div class="icon icon-warning icon-xl icon-circle mb-3">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h2 class="mb-3 display-heading">Test nicht gefunden</h2>
        <p class="mb-4 text-muted">Der angeforderte Test ist nicht verfügbar oder wurde deaktiviert.</p>
        <p>Bitte kontaktieren Sie den Testersteller.</p>
      </div>
      
      <template v-else>
        <div v-if="testStep === 'intro'" class="intro-step animate-fade-in">
          <div class="row justify-content-center">
            <div class="col-md-8">
              <Card animateIn="fade-in-up" class="card-with-form">
                <div class="text-center mb-4">
                  <CompanyLogo
                    :company="companyDataForLogo"
                    :logo-class="'company-logo-medium'"
                    @logo-loaded="handleLogoLoaded"
                    @logo-error="handleLogoError"
                  />
                  <h1 class="mb-2 display-heading">{{ test.name }}</h1>
                  <p class="text-muted meta-stat">
                    <span class="icon icon-muted me-1"><i class="fas fa-question-circle"></i></span> {{ questions.length }} Fragen
                  </p>
                </div>
                
                <div v-if="test.instructions" class="instructions-panel mb-4">
                  <h5 class="section-title">Anweisungen</h5>
                  <div class="instructions-content p-3 bg-light rounded">
                    {{ test.instructions }}
                  </div>
                </div>
                
                <div class="mb-4">
                  <h5 class="section-title">Persönliche Informationen</h5>
                  <p class="text-muted">Bitte geben Sie Ihre Daten ein, um mit dem Test zu beginnen.</p>
                  
                  <form @submit.prevent="startTest" class="form-enhanced">
                    <div class="row mb-3">
                      <div class="col-md-4 mb-3 mb-md-0">
                        <label for="title" class="form-label">Titel (optional)</label>
                        <select
                          id="title"
                          class="form-select with-focus-effect"
                          v-model="userData.title"
                          :class="{ 'is-invalid': errors.title }"
                        >
                          <option value="">Kein Titel</option>
                          <option 
                            v-for="option in titleOptions.filter(option => option.value !== '')" 
                            :key="option.value" 
                            :value="option.value"
                          >
                            {{ option.text }}
                          </option>
                        </select>
                        <div class="invalid-feedback" v-if="errors.title">
                          {{ errors.title }}
                        </div>
                      </div>
                      <div class="col-md-4 mb-3 mb-md-0">
                        <label for="firstName" class="form-label">Vorname</label>
                        <input 
                          type="text" 
                          class="form-control with-focus-effect" 
                          id="firstName" 
                          v-model="userData.first_name"
                          required
                          :class="{ 'is-invalid': errors.first_name }"
                        >
                        <div class="invalid-feedback" v-if="errors.first_name">
                          {{ errors.first_name }}
                        </div>
                      </div>
                      <div class="col-md-4">
                        <label for="lastName" class="form-label">Nachname</label>
                        <input 
                          type="text" 
                          class="form-control with-focus-effect" 
                          id="lastName" 
                          v-model="userData.last_name"
                          required
                          :class="{ 'is-invalid': errors.last_name }"
                        >
                        <div class="invalid-feedback" v-if="errors.last_name">
                          {{ errors.last_name }}
                        </div>
                      </div>
                    </div>
                    
                    <div class="row mb-3">
                      <div class="col-md-6 mb-3 mb-md-0">
                        <label for="email" class="form-label">E-Mail-Adresse</label>
                        <input 
                          type="email" 
                          class="form-control with-focus-effect" 
                          id="email" 
                          v-model="userData.email"
                          required
                          :class="{ 'is-invalid': errors.email }"
                        >
                        <div class="invalid-feedback" v-if="errors.email">
                          {{ errors.email }}
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label for="gender" class="form-label">Geschlecht</label>
                        <select
                          id="gender"
                          class="form-select with-focus-effect"
                          v-model="userData.gender"
                          required
                          :class="{ 'is-invalid': errors.gender }"
                        >
                          <option value="" disabled>Bitte wählen</option>
                          <option 
                            v-for="option in genderOptions" 
                            :key="option.value" 
                            :value="option.value"
                          >
                            {{ option.text }}
                          </option>
                        </select>
                        <div class="invalid-feedback" v-if="errors.gender">
                          {{ errors.gender }}
                        </div>
                      </div>
                    </div>
                    
                    <div class="form-check mb-4">
                      <input 
                        class="form-check-input with-focus-effect" 
                        style="border-radius: 0.5rem; background-color: grey !important; border-color: var(--bs-border-color-translucent) !important;"
                        type="checkbox" 
                        id="dataAgreement"
                        v-model="userData.data_agreement"
                        required
                        :class="{ 'is-invalid': errors.data_agreement }"
                      >
                      <label class="form-check-label" for="dataAgreement">
                        Ich bin damit einverstanden, dass meine Daten zur Auswertung des Tests gespeichert werden.
                      </label>
                      <div class="invalid-feedback" v-if="errors.data_agreement">
                        {{ errors.data_agreement }}
                      </div>
                    </div>
                    
                    <div class="d-grid">
                      <button type="submit" class="btn btn-primary btn-lg btn-with-icon with-hover-effect">
                        <span class="icon me-2"><i class="fas fa-play"></i></span> Test starten
                      </button>
                    </div>
                  </form>
                </div>
              </Card>
            </div>
          </div>
        </div>
        
        <div v-else-if="testStep === 'questions'" class="questions-step animate-fade-in">
          <div class="row justify-content-center">
            <div class="col-md-8">
              <Card animateIn="fade-in-up" class="card-with-form">
                <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                  <h4 class="mb-2 mb-md-0">{{ test.name }}</h4>
                  <div>
                    <span class="badge bg-primary">Frage {{ currentQuestionIndex + 1 }}/{{ questions.length }}</span>
                  </div>
                </div>
                
                <div class="progress progress-enhanced mb-4">
                  <div 
                    class="progress-bar bg-primary" 
                    role="progressbar"
                    :style="{ width: `${(currentQuestionIndex / questions.length) * 100}%` }"
                    :aria-valuenow="(currentQuestionIndex / questions.length) * 100"
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  ></div>
                </div>
                
                <div v-if="currentQuestion" class="current-question animate-fade-in">
                  <QuestionDisplay 
                    :question="currentQuestion"
                    :index="currentQuestionIndex"
                    :total-questions="questions.length"
                    :user-answers="selectedOptions"
                    @update:user-answers="updateSelectedOptions"
                    @update:text-answer="updateTextAnswer"
                  />
                  
                  <div class="d-flex justify-content-end mt-4">
                    <button
                      v-if="currentQuestionIndex > 0"
                      class="btn btn-outline-secondary btn-with-icon with-hover-effect me-2"
                      @click="previousQuestion"
                    >
                      <span class="icon me-1"><i class="fas fa-arrow-left"></i></span> Zurück
                    </button>
                    <button 
                      class="btn btn-primary btn-with-icon with-hover-effect" 
                      @click="nextQuestion"
                    >
                      <span v-if="currentQuestionIndex < questions.length - 1">
                        Weiter <span class="icon ms-1"><i class="fas fa-arrow-right"></i></span>
                      </span>
                      <span v-else>
                        Test abschließen <span class="icon ms-1"><i class="fas fa-check"></i></span>
                      </span>
                    </button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
        
        <div v-else-if="testStep === 'completed'" class="completed-step animate-fade-in">
          <div class="row justify-content-center">
            <div class="col-md-8">
              <Card animateIn="fade-in-up" class="card-with-form">
                <div class="text-center mb-4">
                  <span class="icon icon-success icon-xl icon-circle mb-3 pulse-animation">
                    <i class="fas fa-check-circle"></i>
                  </span>
                  <h2 class="mb-2 display-heading">Test abgeschlossen!</h2>
                  <p class="mb-4 text-muted">Vielen Dank für die Teilnahme an diesem Test.</p>
                </div>
                
                <div class="alert alert-success alert-with-icon mb-4">
                  <span class="alert-icon"><i class="fas fa-info-circle"></i></span>
                  <div>
                    <h5 class="alert-heading">Ihre Antworten wurden gespeichert</h5>
                    <p class="mb-0">Die Ergebnisse wurden erfolgreich übermittelt und gespeichert. Der Testersteller kann diese jetzt einsehen.</p>
                  </div>
                </div>
                
                <!-- Anzeige der Testergebnisse, wenn erlaubt -->
                <div v-if="test.show_results && submissionResult">
                  <hr class="my-4">
                  <TestResults 
                    :questions="questions" 
                    :answers="submissionResult.answers || answers" 
                    :score="submissionResult.score"
                    :max-score="submissionResult.max_score"
                    :percentage="submissionResult.score_percentage"
                    :showDetails="true"
                  />
                </div>
                
                <div class="d-grid mt-4">
                  <p class="mb-0">
                    <i class="fas fa-info-circle me-3"></i>
                    Sie können diese Seite jetzt schließen.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
        
        <div v-else-if="testStep === 'error'" class="error-step animate-fade-in">
          <div class="row justify-content-center">
            <div class="col-md-8">
              <Card animateIn="fade-in-up" class="card-with-form">
                <div class="text-center mb-4">
                  <span class="icon icon-danger icon-xl icon-circle mb-3">
                    <i class="fas fa-exclamation-circle"></i>
                  </span>
                  <h2 class="mb-2 display-heading">Fehler bei der Übermittlung</h2>
                  <p class="mb-4 text-muted">Leider ist ein Fehler aufgetreten. Ihre Antworten konnten nicht gespeichert werden.</p>
                </div>
                
                <div class="alert alert-danger alert-with-icon mb-4">
                  <span class="alert-icon"><i class="fas fa-exclamation-triangle"></i></span>
                  <div>
                    <h5 class="alert-heading">Technisches Problem</h5>
                    <p class="mb-0">Bitte versuchen Sie es später erneut oder kontaktieren Sie den Testersteller.</p>
                  </div>
                </div>
                
                <div class="d-flex gap-3">
                  <button 
                    class="btn btn-primary btn-with-icon with-hover-effect flex-fill" 
                    @click="submitAnswers"
                    :disabled="submitting"
                  >
                    <span class="icon me-2"><i class="fas fa-redo"></i></span> 
                    <span v-if="!submitting">Erneut versuchen</span>
                    <span v-else>Wird gesendet...</span>
                  </button>
                  <router-link to="/" class="btn btn-outline-secondary btn-with-icon with-hover-effect flex-fill">
                    <span class="icon me-2"><i class="fas fa-home"></i></span> Zur Startseite
                  </router-link>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import http from '@/services/http';
import Card from '@/components/ui/Card.vue';
import CompanyLogo from '@/components/ui/CompanyLogo.vue';
import QuestionDisplay from '@/views/tests/components/QuestionDisplay.vue';
import TestResults from '@/views/tests/components/TestResults.vue';

export default {
  name: 'TestExecute',
  components: {
    Card,
    CompanyLogo,
    QuestionDisplay,
    TestResults
  },
  data() {
    return {
      loading: true,
      testFound: true,
      test: {},
      questions: [],
      currentQuestionIndex: 0,
      selectedOptions: [],
      openAnswer: '',
      answers: [],
      testStep: 'intro',
      score: 0,
      testCompleted: false,
      submissionResult: null,
      userData: {
        title: '',
        first_name: '',
        last_name: '',
        email: '',
        gender: '',
        data_agreement: false
      },
      genderOptions: [
        { text: 'Mann', value: 'male' },
        { text: 'Frau', value: 'female' },
        { text: 'Divers', value: 'diverse' }
      ],
      titleOptions: [
        { text: 'Kein Titel', value: '' },
        { text: 'Dr.', value: 'Dr.' },
        { text: 'Prof.', value: 'Prof.' },
        { text: 'Prof. Dr.', value: 'Prof. Dr.' },
        { text: 'Dr. med.', value: 'Dr. med.' },
        { text: 'Dr. rer. nat.', value: 'Dr. rer. nat.' },
        { text: 'Dr. phil.', value: 'Dr. phil.' },
        { text: 'Dipl.-Ing.', value: 'Dipl.-Ing.' },
        { text: 'M.Sc.', value: 'M.Sc.' },
        { text: 'B.Sc.', value: 'B.Sc.' }
      ],
      errors: {},
      submitting: false,
      companyLogoStatus: 'loading'
    };
  },
  computed: {
    testHash() {
      return this.$route.params.hash;
    },
    currentQuestion() {
      if (!this.questions.length || this.currentQuestionIndex >= this.questions.length) {
        return null;
      }
      return this.questions[this.currentQuestionIndex];
    },
    companyDataForLogo() {
      let data = {}; // Initialize empty
      if (this.test?.company?.logo_url) {
        data = {
          logo_url: this.test.company.logo_url,
          name: this.test.company.name || 'Company Logo'
        };
      } else if (this.test?.logo_url) {
        data = {
          logo_url: this.test.logo_url,
          name: this.test.company_name || this.test.name || 'Company Logo'
        };
      }
      // Add console log here
      console.log('TestExecute: companyDataForLogo computed:', JSON.stringify(data)); 
      return data;
    }
  },
  methods: {
    async loadTest() {
      this.loading = true;
      
      try {
        const response = await http.get(`/api/test/${this.testHash}`);
        
        if (response.data.success) {
          this.test = response.data.test;
          this.questions = response.data.questions || [];
          
          // Antworten-Array mit korrekter Struktur initialisieren
          this.answers = this.questions.map(question => ({
            question_id: question.id,
            selected_options: [0], // Standardwert ist [0] statt leeres Array
            text_answer: '',
            points_earned: 0,
            max_points: question.points || 0
          }));
          
          this.testFound = true;
          console.log('TestExecute: Test data loaded:', JSON.stringify(this.test)); // Log loaded test data
        } else {
          this.errorMessage = response.data.message || 'Test nicht gefunden';
          this.testFound = false;
        }
      } catch (error) {
        console.error('Fehler beim Laden des Tests:', error);
        this.errorMessage = error.response?.data?.message || 'Der Test konnte nicht geladen werden.';
        this.testFound = false;
      } finally {
        this.loading = false;
      }
    },
    startTest() {
      // Formularvalidierung
      this.errors = {};
      let isValid = true;
      
      if (!this.userData.first_name.trim()) {
        this.errors.first_name = 'Vorname ist erforderlich';
        isValid = false;
      }
      
      if (!this.userData.last_name.trim()) {
        this.errors.last_name = 'Nachname ist erforderlich';
        isValid = false;
      }
      
      if (!this.userData.email.trim()) {
        this.errors.email = 'E-Mail ist erforderlich';
        isValid = false;
      } else if (!this.validateEmail(this.userData.email)) {
        this.errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
        isValid = false;
      }
      
      // Validierung für Geschlecht
      if (!this.userData.gender) {
        this.errors.gender = 'Bitte wählen Sie Ihr Geschlecht';
        isValid = false;
      }
      
      if (!this.userData.data_agreement) {
        this.errors.data_agreement = 'Bitte stimmen Sie der Datenspeicherung zu';
        isValid = false;
      }
      
      if (isValid) {
        this.testStep = 'questions';
        this.currentQuestionIndex = 0;
        this.resetQuestionState();
      }
    },
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    resetQuestionState() {
      this.selectedOptions = [];
      this.openAnswer = '';
    },
    updateSelectedOptions(options) {
      console.log("Ausgewählte Optionen aktualisiert:", options);
      this.selectedOptions = [...options];
    },
    updateTextAnswer(text) {
      console.log("Textantwort aktualisiert:", text);
      this.openAnswer = text;
    },
    toggleOption(optionIndex) {
      const index = this.selectedOptions.indexOf(optionIndex);
      
      if (index === -1) {
        this.selectedOptions.push(optionIndex);
      } else {
        this.selectedOptions.splice(index, 1);
      }
    },
    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        // Aktuelle Antwort speichern, bevor man zurückgeht?
        // this.saveCurrentAnswer(); // Optional: Entscheiden, ob beim Zurückgehen gespeichert wird
        this.currentQuestionIndex--;
        this.resetQuestionState();
        this.loadQuestionState();
      }
    },
    nextQuestion() {
      // Aktuelle Antwort speichern
      this.saveCurrentAnswer();
      
      // Zur nächsten Frage wechseln oder Test abschließen
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
        this.resetQuestionState();
        this.loadQuestionState();
      } else {
        this.submitAnswers();
      }
    },
    async submitAnswers() {
      this.submitting = true;
      
      try {
        this.saveCurrentAnswer();
        
        // Antworten formatieren und debuggen
        console.log('Formatierung gestartet. Antworten:', JSON.parse(JSON.stringify(this.answers)));
        
        const formattedAnswers = this.answers.map((answer, index) => {
          const question = this.questions[index];
          if (!question || !answer) return null;
          
          // Für Debugging: Ausgabe der selected_options
          console.log(`Frage ${index+1}:`, question.description);
          console.log(`Selected options vorher:`, answer.selected_options);
          
          if (question.type === 'multiple_choice') {
            // Stelle sicher, dass selected_options ein Array ist und keine 0-Werte enthält
            let selectedOptions = [];
            
            if (Array.isArray(answer.selected_options)) {
              selectedOptions = answer.selected_options.filter(opt => opt !== 0 && opt !== null);
            }
            
            console.log(`Selected options nachher:`, selectedOptions);
            
            return {
              question_id: question.id,
              selected_options: selectedOptions.length > 0 ? selectedOptions : [0], // Mindestens [0]
              text_answer: '',
              max_points: question.points || 0
            };
          } else {
            return {
              question_id: question.id,
              selected_options: [0], // Standardwert [0] für Textfragen
              text_answer: answer.text_answer || '',
              max_points: question.points || 0
            };
          }
        }).filter(answer => answer !== null);
        
        console.log('Daten zum Senden:', {
          answers: formattedAnswers
        });

        const response = await http.post(`/api/test/${this.testHash}/create`, {
          answers: formattedAnswers,
          title: this.userData.title,
          first_name: this.userData.first_name,
          last_name: this.userData.last_name,
          email: this.userData.email,
          gender: this.userData.gender,
          data_agreement: this.userData.data_agreement
        });
        
        if (response.data.success) {
          this.submissionResult = response.data.entry;
          
          // Die ausgewählten Antworten dem submissionResult hinzufügen
          this.submissionResult.answers = this.submissionResult.answers.map((backendAnswer, index) => {
            const question = this.questions[index];
            const userAnswer = this.answers[index];
            
            console.log(`Verarbeite Antwort ${index} vom Backend:`, backendAnswer);
            console.log(`Original Benutzerantwort:`, userAnswer);
            
            // Stelle sicher, dass die Antworten mit den richtigen Fragen verknüpft sind
            if (!backendAnswer.question && question) {
              backendAnswer.question = question;
            }
            
            // Stelle sicher, dass die ausgewählten Optionen korrekt formatiert sind
            let selectedOptions = [];
            
            if (Array.isArray(backendAnswer.selected_options)) {
              selectedOptions = backendAnswer.selected_options.filter(opt => opt !== 0 && opt !== null);
            } else if (typeof backendAnswer === 'object') {
              // Prüfe auf answer_X_selected Eigenschaften
              for (let i = 1; i <= 6; i++) {
                if (backendAnswer[`answer_${i}_selected`] === true) {
                  selectedOptions.push(i);
                }
              }
            }
            
            // Debug-Ausgabe
            console.log(`Finale ausgewählte Optionen für Antwort ${index}:`, selectedOptions);
            
            // Stelle sicher, dass die Frage-Informationen vollständig sind (z.B. allow_multiple_answers)
            let questionData = backendAnswer.question || question;
            
            // Dies ist kritisch: Wenn der Typ nicht übereinstimmt, könnte es zu falschen Anzeigen kommen
            if (questionData && question) {
              questionData = {
                ...questionData,
                // Stelle sicher, dass diese wichtigen Eigenschaften vorhanden sind
                type: questionData.type || question.type,
                allow_multiple_answers: questionData.allow_multiple_answers !== undefined ? 
                                      questionData.allow_multiple_answers : question.allow_multiple_answers
              };
              
              // Stelle sicher, dass alle "correct_X" und "is_answer_X_correct" Felder vorhanden sind
              for (let i = 1; i <= 6; i++) {
                // Wenn in der Frage vom Backend bereits correct_X existiert, verwende dieses
                if (questionData[`correct_${i}`] === undefined &&
                    question[`correct_${i}`] !== undefined) {
                  questionData[`correct_${i}`] = question[`correct_${i}`];
                }
                
                // Wenn in der Frage vom Backend bereits is_answer_X_correct existiert, verwende dieses
                if (questionData[`is_answer_${i}_correct`] === undefined &&
                    question[`is_answer_${i}_correct`] !== undefined) {
                  questionData[`is_answer_${i}_correct`] = question[`is_answer_${i}_correct`];
                }
              }
            }
            
            return {
              ...backendAnswer,
              // Verwende die verbesserte Frage
              question: questionData,
              // Falls wir keine selected_options haben, übernehme die vom Benutzer gespeicherten
              selected_options: selectedOptions.length > 0 ? selectedOptions : 
                (userAnswer && userAnswer.selected_options ? userAnswer.selected_options : [0])
            };
          });
          
          console.log('Finale Antworten für TestResults:', this.submissionResult.answers);
          
          this.testStep = 'completed';
        } else {
          this.testStep = 'error';
        }
      } catch (error) {
        console.error('Fehler beim Absenden des Tests:', error);
        this.testStep = 'error';
      } finally {
        this.submitting = false;
      }
    },
    saveCurrentAnswer() {
      if (!this.currentQuestion) return;
      
      if (this.currentQuestion.type === 'multiple_choice') {
        this.answers[this.currentQuestionIndex] = {
          question_id: this.currentQuestion.id,
          selected_options: this.selectedOptions.length > 0 ? [...this.selectedOptions] : [0], // Mindestens [0]
          text_answer: '',
          points_earned: 0,
          max_points: this.currentQuestion.points || 0
        };
      } else {
        this.answers[this.currentQuestionIndex] = {
          question_id: this.currentQuestion.id,
          selected_options: [0], // Standardwert [0] für Textfragen
          text_answer: this.openAnswer || '',
          points_earned: 0,
          max_points: this.currentQuestion.points || 0
        };
      }
    },
    loadQuestionState() {
      if (!this.currentQuestion) return;
      
      const savedAnswer = this.answers[this.currentQuestionIndex];
      if (!savedAnswer) return;
      
      if (this.currentQuestion.type === 'multiple_choice') {
        // Versuche zuerst selected_options, dann answer_selected
        if (Array.isArray(savedAnswer.selected_options)) {
          this.selectedOptions = [...savedAnswer.selected_options];
        } else if (savedAnswer.selected_options) {
          this.selectedOptions = savedAnswer.selected_options.split(',').map(Number);
        } else {
          this.selectedOptions = [];
        }
      } else {
        // Versuche zuerst text_answer, dann answer_text
        this.openAnswer = savedAnswer.text_answer || '';
      }
    },
    getQuestionOptions(question) {
      const options = [];
      for (let i = 1; i <= 6; i++) {
        const answer = question[`answer_${i}`];
        if (answer) {
          options.push(answer);
        }
      }
      return options;
    },
    handleLogoLoaded() {
      this.companyLogoStatus = 'loaded';
      // Add console log here
      console.log('TestExecute: handleLogoLoaded called. Status:', this.companyLogoStatus);
    },
    handleLogoError(event) {
      this.companyLogoStatus = 'error';
      // Add console log here
      console.error('TestExecute: handleLogoError called. Status:', this.companyLogoStatus, 'Event:', event);
    }
  },
  created() {
    this.loadTest();
  }
};
</script>

<style scoped>
/* Hier vorhandene lokale Stile für TestExecute */

.test-execute-container {
  /* Add styles for the main container if needed */
}

.content-loading,
.not-found-container {
  /* Styles for loading and not found states */
}

.intro-step,
.questions-step,
.completed-step,
.error-step {
  /* Styles for different steps */
}

.display-heading {
  font-weight: 600; /* Example style */
}

.meta-stat {
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center; /* Center align meta stats */
}

.instructions-panel {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid var(--bs-border-color-translucent);
  padding-bottom: 0.5rem;
}

.instructions-content {
  font-size: 0.95rem;
  line-height: 1.6;
}

.card-with-form {
  padding: 2rem; /* Add padding to the card */
}

/* Styles for enhanced form elements */
.form-enhanced {
  .form-control,
  .form-select {
    border-radius: 0.5rem; /* Weiche Ecken */
    padding: 0.75rem 1rem; /* Gutes Padding */
    border: 1px solid var(--bs-border-color-translucent); /* Subtiler Rand */
    background-color: var(--bs-body-bg); /* Hintergrund anpassen */
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:focus {
      border-color: var(--bs-primary);
      box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.2);
      background-color: var(--bs-body-bg); /* Fokus-Hintergrund beibehalten */
    }
  }
  
  /* Optional: Etwas mehr Abstand zwischen Label und Input */
  .form-label {
      margin-bottom: 0.5rem; 
  }
}

/* Styles for QuestionDisplay specific elements if needed */

.progress-enhanced {
  height: 10px; /* Example style */
  border-radius: 5px;
}

.current-question {
  margin-top: 1.5rem;
}

.alert-with-icon {
  display: flex;
  align-items: flex-start;
}

.alert-icon {
  margin-right: 1rem;
  font-size: 1.5rem;
  margin-top: 0.1rem; /* Align icon slightly */
}

.icon-circle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 4rem;
    height: 4rem;
    border-radius: 50%;
    background-color: rgba(var(--bs-primary-rgb), 0.1);
    color: var(--bs-primary);
}

.icon-circle.icon-success {
    background-color: rgba(var(--bs-success-rgb), 0.1);
    color: var(--bs-success);
}

.icon-circle.icon-danger {
    background-color: rgba(var(--bs-danger-rgb), 0.1);
    color: var(--bs-danger);
}

.icon-circle.icon-warning {
    background-color: rgba(var(--bs-warning-rgb), 0.1);
    color: var(--bs-warning);
}

/* Animation for pulse */
@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(var(--bs-success-rgb), 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(var(--bs-success-rgb), 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(var(--bs-success-rgb), 0);
  }
}

.pulse-animation {
  animation: pulse 1.5s infinite;
}

/* Button hover effects */
.btn.with-hover-effect {
  transition: transform 0.2s ease-out, box-shadow 0.2s ease-out;
}

.btn.with-hover-effect:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.company-logo-medium {
    max-height: 60px; /* Adjust size as needed */
    max-width: 200px;
    object-fit: contain;
}

</style> 